import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { ThemeContext } from 'styled-components';
import { HorizontalScroll } from '@luigiclaudio/ga-baseline-ui/horizontal-scroll';
import { Button } from '@luigiclaudio/ga-baseline-ui/button';
import { H6 } from '@luigiclaudio/ga-baseline-ui/typography';
import ColorToolsContext from '../../context/ColorToolsContext';
import colorToolsUtils from '../../../utils/colorToolsUtils';
import UseToolsHorizontalScrollContainer from './UseToolsHorizontalScrollContainer';
import UseToolsContainer from './UseToolsContainer';
import UseToolsHorizontalScrollItem from './UseToolsHorizontalScrollItem';
import UseToolsHorizontalScrollList from './UseToolsHorizontalScrollList';
import { defaultStepValue } from '../../../data/stepList';

const UseToolsHorizontalScroll = ({ colorValue, onClickCallback }) => {
    const theme = useContext(ThemeContext);
    const intl = useIntl();
    const {
        setColorOnStage,
        setColorMixedOnStage,
        colorMixedOnStage,
        stepsOnStage,
        setStepsOnStage,
    } = useContext(ColorToolsContext);

    const commonActionToolProperties = {
        btnType: 'outlined',
        skin: 'muted',
        size: 'sm',
    };

    const actionToolsList = [
        {
            label: intl.formatMessage({ id: 'useTools.colorHarmonies.label' }),
            onClick: () => {
                navigate('/color-harmonies/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.colorStyleGuide.label' }),
            onClick: () => {
                navigate('/color-style-guide/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.colorWheel.label' }),
            onClick: () => {
                navigate('/color-wheel/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.colorDetails.label' }),
            onClick: () => {
                navigate('/color-details/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.colorMixer.label' }),
            onClick: () => {
                navigate('/color-mixer/');
                if (!colorMixedOnStage) {
                    setColorMixedOnStage(colorToolsUtils.generateColor());
                }
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.colorTones.label' }),
            onClick: () => {
                navigate('/color-tones/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.tintsAndShades.label' }),
            onClick: () => {
                navigate('/tints-and-shades/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.lightenAndDarken.label' }),
            onClick: () => {
                navigate('/lighten-and-darken/');
            },
        },
        {
            label: intl.formatMessage({ id: 'useTools.theme.label' }),
            onClick: () => {
                navigate('/color-theme/');
            },
        },
    ];

    return (
        <UseToolsContainer>
            <H6 color="muted">
                <FormattedMessage id="useTools.horizontalScroll.title" />
            </H6>
            <UseToolsHorizontalScrollContainer>
                <HorizontalScroll
                    scrollButtonColor={theme.modal.backgroundColor}
                    scrollButton
                    fadeColor={theme.modal.backgroundColor}
                >
                    <UseToolsHorizontalScrollList>
                        {actionToolsList.map((item, index) => {
                            const { label, onClick } = item;
                            return (
                                <UseToolsHorizontalScrollItem
                                    key={index}
                                    isFirst={index === 0}
                                    isLast={index + 1 === item.length}
                                >
                                    <Button
                                        onClick={() => {
                                            setColorOnStage(colorValue.slice(1));
                                            if (!stepsOnStage) {
                                                setStepsOnStage(defaultStepValue);
                                            }
                                            onClickCallback();
                                            onClick();
                                        }}
                                        {...commonActionToolProperties}
                                    >
                                        {label}
                                    </Button>
                                </UseToolsHorizontalScrollItem>
                            );
                        })}
                    </UseToolsHorizontalScrollList>
                </HorizontalScroll>
            </UseToolsHorizontalScrollContainer>
        </UseToolsContainer>
    );
};

UseToolsHorizontalScroll.propTypes = {
    colorValue: PropTypes.string.isRequired,
    onClickCallback: PropTypes.func,
};

UseToolsHorizontalScroll.defaultProps = {
    onClickCallback: () => {},
};

export default UseToolsHorizontalScroll;
